/** Figures for the Members list and Member page — the same filters, sums and sort order as the original views. */
import type { Backup, Member, WorkLog } from '@/types/models';
import { byDateDesc, sum } from '@/domain/format';
import { inRange, type DateRange } from '@/domain/periods';

export interface MemberRow {
  member: Member;
  /** hours in the selected period */
  hours: number;
  /** hours, all time */
  hoursAll: number;
  /** distinct days worked in the period */
  days: number;
  /** latest work-log date (all time), '' when never */
  lastDate: string;
  /** projects worked on in the period ('' = General) */
  projectIds: string[];
}

/** Members page rows: search on name + role, most hours in the period first, then all-time hours, then name. */
export function memberRows(data: Pick<Backup, 'members' | 'logs'>, query: string, range: DateRange): MemberRow[] {
  const s = query.toLowerCase();
  return data.members
    .filter((m) => !s || (m.name + ' ' + (m.role || '')).toLowerCase().includes(s))
    .map((m) => {
      const ml = data.logs.filter((l) => l.memberId === m.id);
      const inR = ml.filter((l) => inRange(l.date, range));
      return {
        member: m,
        hours: sum(inR.map((l) => l.hours)),
        hoursAll: sum(ml.map((l) => l.hours)),
        days: new Set(inR.map((l) => l.date)).size,
        lastDate: [...ml].sort(byDateDesc)[0]?.date || '',
        projectIds: [...new Set(inR.map((l) => l.projectId))],
      };
    })
    .sort((a, b) => b.hours - a.hours || b.hoursAll - a.hoursAll || a.member.name.localeCompare(b.member.name));
}

/** Member page: the member's logs (newest first), hours per project and per day. */
export function memberSummary(allLogs: WorkLog[], memberId: string) {
  const logs = allLogs.filter((l) => l.memberId === memberId).sort(byDateDesc);
  const byProject: Record<string, number> = {};
  const byDay: Record<string, number> = {};
  logs.forEach((l) => {
    byProject[l.projectId] = (byProject[l.projectId] || 0) + Number(l.hours);
    byDay[l.date] = (byDay[l.date] || 0) + Number(l.hours);
  });
  const total = sum(logs.map((l) => l.hours));
  const days = Object.keys(byDay).length;
  const projects = Object.entries(byProject).sort((a, b) => b[1] - a[1]);
  return { logs, total, days, projects, projectHours: sum(Object.values(byProject)) };
}
